import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */

import { Box, Text, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import Button, { LinkButton } from '@atlaskit/button/new';
import React, { Fragment, useState, useEffect } from 'react';
import { injectIntl } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import TRELLO_ERROR_SRC from '../../assets/trello-error';
import UNVERIFIED_ERROR_SRC from '../../assets/unverified-error';
import { NAVIGATION_CHANNEL, OPERATIONAL_EVENT_TYPE, UI_EVENT_TYPE, withAnalyticsEvents } from '../../common/utils/analytics';
import { getLoginUrl } from '../../common/utils/environment';
import { errorToReason } from '../../common/utils/error-to-reason';
import messages from '../../common/utils/messages';
import { Product } from '../../types';
import { FormattedMessage } from 'react-intl-next';
import { SwitcherLoadError } from '../../common/utils/errors/switcher-load-error';
import { FETCH_ERROR_NAME } from '../../common/utils/errors/fetch-error';
const TRIGGER_SUBJECT = 'errorBoundary';
const ACTION_SUBJECT = 'rendered';
// This image is also used as the generic error message in Notifications

const robotsImgStyles = css({
  height: 200
});
const trelloErrorImgStyles = css({
  width: 140,
  height: 88
});
const unverifiedUserImageStyles = css({
  height: 150
});
const errorContentStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column'
});
const errorHeadingStyles = xcss({
  maxWidth: '240',
  marginTop: 'space.400'
});
const errorMessageStyles = xcss({
  maxWidth: '240',
  marginTop: 'space.300'
});
const errorBoundaryBoxStyles = css({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  textAlign: 'center',
  margin: `${"var(--ds-space-600, 48px)"} 0`
});
const loginButtonStyles = xcss({
  marginTop: 'space.300',
  justifyContent: 'center',
  alignSelf: 'stretch'
});
const ErrorMessage = props => jsx(Box, {
  xcss: errorMessageStyles
}, jsx(Text, {
  as: "p"
}, props.children));
class ErrorBoundary extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      hasError: false
    });
    _defineProperty(this, "fireOperationalEvent", payload => {
      if (this.props.createAnalyticsEvent) {
        this.props.createAnalyticsEvent({
          eventType: OPERATIONAL_EVENT_TYPE,
          actionSubject: this.props.triggerSubject || TRIGGER_SUBJECT,
          ...payload
        }).fire(NAVIGATION_CHANNEL);
      }
    });
    _defineProperty(this, "handleLogin", (_, analyticsEvent) => {
      analyticsEvent.update({
        eventType: UI_EVENT_TYPE,
        actionSubjectId: 'login'
      }).fire(NAVIGATION_CHANNEL);
      if (this.props.product !== Product.TRELLO) {
        window.location.reload();
      }
    });
  }
  componentDidCatch(error) {
    var _this$props$onErrorCa, _this$props;
    const reason = error instanceof SwitcherLoadError ? error.reason() : errorToReason(error);
    this.setState({
      hasError: true,
      reason
    }, () => {
      this.fireOperationalEvent({
        action: ACTION_SUBJECT,
        attributes: {
          ...reason,
          ...this.props.extraAnalyticProps
        }
      });
    });
    (_this$props$onErrorCa = (_this$props = this.props).onErrorCallback) === null || _this$props$onErrorCa === void 0 ? void 0 : _this$props$onErrorCa.call(_this$props, error);
  }
  renderErrorBody() {
    const {
      reason
    } = this.state;
    if (!reason) {
      return;
    }
    if (reason.name === FETCH_ERROR_NAME) {
      const isTrello = this.props.product === Product.TRELLO;
      if (reason.status === 401) {
        // Not authorised http error
        return jsx(Fragment, null, jsx(ErrorMessage, null, jsx(FormattedMessage, messages.errorTextLoggedOut)), isTrello ? jsx(Box, {
          xcss: loginButtonStyles
        }, jsx(LinkButton, {
          href: getLoginUrl(Product.TRELLO),
          onClick: this.handleLogin
        }, jsx(FormattedMessage, messages.login))) : jsx(Box, {
          xcss: loginButtonStyles
        }, jsx(Button, {
          onClick: this.handleLogin
        }, jsx(FormattedMessage, messages.login))));
      }
      if (reason.status === 403) {
        return jsx(ErrorMessage, null, jsx(FormattedMessage, messages.errorTextUnverified));
      }
      return jsx(ErrorMessage, null, jsx(FormattedMessage, messages.errorTextNetwork));
    }

    // Default error message
    return jsx(ErrorMessage, null, jsx(FormattedMessage, messages.errorText));
  }
  renderErrorHeading() {
    const {
      reason
    } = this.state;
    if (!reason) {
      return;
    }
    if (reason.name === FETCH_ERROR_NAME && reason.status === 401) {
      return jsx(FormattedMessage, messages.errorHeadingLoggedOut);
    }
    if (reason.name === FETCH_ERROR_NAME && reason.status === 403) {
      return jsx(FormattedMessage, messages.errorHeadingUnverified);
    }
    return jsx(FormattedMessage, messages.errorHeading);
  }
  renderErrorImage() {
    const {
      intl,
      product
    } = this.props;
    const {
      reason
    } = this.state;
    if (product === Product.TRELLO) {
      return jsx("img", {
        css: trelloErrorImgStyles,
        src: TRELLO_ERROR_SRC,
        alt: intl.formatMessage(messages.errorImageAltText)
      });
    }
    if (reason.status === 403) {
      return jsx("img", {
        css: unverifiedUserImageStyles,
        src: UNVERIFIED_ERROR_SRC,
        alt: intl.formatMessage(messages.errorImageAltText)
      });
    }
    return jsx(RobotImage, {
      css: robotsImgStyles,
      alt: intl.formatMessage(messages.errorImageAltText)
    });
  }
  render() {
    const {
      hideFallbackUI
    } = this.props;
    const {
      hasError
    } = this.state;
    if (hasError && hideFallbackUI) {
      return jsx(React.Fragment, null);
    }
    if (hasError) {
      return jsx("div", {
        css: errorBoundaryBoxStyles,
        "data-testid": "error-boundary"
      }, jsx("div", {
        css: errorContentStyles
      }, this.renderErrorImage(), jsx(Box, {
        xcss: errorHeadingStyles
      }, jsx(Heading, {
        size: "medium"
      }, this.renderErrorHeading())), this.renderErrorBody()));
    }
    return this.props.children;
  }
}
const RobotImage = props => {
  const [imgSrc, setImgSrc] = useState();
  useEffect(() => {
    import( /* webpackChunkName: "@ak-switcher-chunk-robot-error-img" */'../../assets/robot-error').then(src => setImgSrc(src.default));
  }, []);
  // eslint-disable-next-line jsx-a11y/alt-text
  return jsx("img", _extends({}, props, {
    src: imgSrc
  }));
};
export default withAnalyticsEvents()(injectIntl(ErrorBoundary));